import React, {useCallback, useState} from "react"
import Flex from "@irati/ui/src/Flex"
import IconButton from "@irati/ui/src/Button/IconButton"
import devToggles from "@irati/dev-toggles"
import SearchBarComponent from "@irati-web/platform/src/components/Header/shared/SearchBarComponent"
import tracking from "@irati-tracking/wellness"
import FormattedMessage from "@irati/utils/lang"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import SearchModal from "@irati-web/search/src/components/Popups/SearchBar/styled/SearchModal"
import styled from "styled-components"
import Input from "@irati/ui/src/FormItem/Input"
import History, {useAppRouteContext} from "@irati/utils/history"
import {Hr} from "@irati/ui/src/Layout"

import Loading from "@irati/ui/src/Loading"
import {isSearchTermValid, MIN_SEARCH_LENGTH, useWellnessSearch} from "@irati/wellness/hooks/useWellnessSearch"
import {create} from "zustand"
import ErrorText from "@irati/ui/src/ErrorText"
import {WellnessSearchResultList} from "./WellnessSearchList"

const MAX_RESULTS = 4

const useSearchStore = create<{
  searchTerm: string
  hasInvalidSearch: boolean
  setSearchTerm: (searchTerm: string) => void
  clearSearchTerm: () => void
  setInvalidSearchError: (error: boolean) => void
}>()((set) => ({
  searchTerm: "",
  setSearchTerm: (searchTerm) => set({searchTerm, hasInvalidSearch: false}),
  clearSearchTerm: () => set({searchTerm: ""}),
  setInvalidSearchError: (hasInvalidSearch) => set({hasInvalidSearch}),
  hasInvalidSearch: false
}))

export function WellnessSearch() {
  const showNewWellness = devToggles.showNewWellness()
  const [showSearchBar, setShowSearchBar] = useState(false)
  const clearSearchTerm = useSearchStore((state) => state.clearSearchTerm)

  const toggleSearch = useCallback(() => {
    if (!showSearchBar) {
      tracking.startWellnessSearch()
    } else {
      clearSearchTerm()
    }
    setShowSearchBar((current) => !current)
  }, [showSearchBar, clearSearchTerm])

  if (!showNewWellness) return null

  return (
    <Flex direction="column" marginTop={2}>
      <Flex alignItems="center" onClick={toggleSearch}>
        <SearchBarComponent messageId="wellness.search.placeholder" />
      </Flex>
      <WellnessSearchBar show={showSearchBar} onClose={toggleSearch}>
        <WellnessSearchResults onClose={toggleSearch} />
      </WellnessSearchBar>
    </Flex>
  )
}

interface Props {
  show: boolean
  onClose: () => void
  children: React.ReactNode
}

function WellnessSearchBar({show, onClose, children}: Props) {
  const {app, apps} = useAppRouteContext()
  const searchTerm = useSearchStore((state) => state.searchTerm)
  const setSearchTerm = useSearchStore((state) => state.setSearchTerm)
  const setInvalidSearchError = useSearchStore((state) => state.setInvalidSearchError)
  const invalidSearchError = useSearchStore((state) => state.hasInvalidSearch)

  return (
    <SearchModal show={show} className="search-popup searchModal" onHide={onClose} width="60%">
      <div className="searchModal_wrapper">
        <Flex alignItems="center" justifyContent="space-between">
          <Flex alignItems="center" flex="1">
            <Icon icon={IconTypes.search} size={24} />
            <StyledInput
              autocomplete="off"
              inputRef={(input) => input?.focus()}
              onChange={(value) => setSearchTerm(value)}
              value={searchTerm}
              name="search"
              flush
              hideBorder
              label="wellness.search.label"
              hideLabel
              placeholder="wellness.search.placeholder"
              onEnter={() => {
                if (isSearchTermValid(searchTerm)) {
                  History.push(`/${apps[app]}/search?q=${encodeURIComponent(searchTerm)}`)
                } else {
                  setInvalidSearchError(true)
                }
              }}
            />
          </Flex>
          <IconButton icon={IconTypes.close} onClick={onClose} />
        </Flex>

        {children}
        {invalidSearchError ? (
          <ErrorText>
            <FormattedMessage id="wellness.search.invalidSearchError" values={{searchTerm, minLength: MIN_SEARCH_LENGTH}} />
          </ErrorText>
        ) : null}
      </div>
    </SearchModal>
  )
}

export function WellnessSearchResults({onClose}: {onClose: () => void}) {
  const searchTerm = useSearchStore((state) => state.searchTerm)
  const searchInput = React.useMemo(() => ({search: searchTerm, limit: MAX_RESULTS}), [searchTerm])
  const wellnessSearch = useWellnessSearch(searchInput)

  return (
    <Flex direction="column" gap={2} marginTop={2}>
      <Hr solid flush />
      <Loading load={wellnessSearch.load}>
        <WellnessSearchResultList
          results={wellnessSearch.data?.results.slice(0, MAX_RESULTS)}
          count={wellnessSearch.data?.total ?? 0}
          onClose={onClose}
          searchTerm={wellnessSearch.variables?.search ?? ""}
          maxResults={MAX_RESULTS}
        />
      </Loading>
    </Flex>
  )
}

const StyledInput = styled(Input)`
  flex: 1;
`
